import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Product } from "@/entities/Product";
import { Creator } from "@/entities/Creator";
import { Star, ShoppingBag, ArrowLeft, MapPin, Package, ChevronLeft, ChevronRight, Check } from "lucide-react";

function StarRating({ rating, size = "sm" }) {
  const sz = size === "lg" ? "w-4 h-4" : "w-3 h-3";
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} className={`${sz} ${s <= Math.round(rating) ? "fill-[#c8a96e] text-[#c8a96e]" : "text-gray-300"}`} />
      ))}
    </div>
  );
}

export default function ProductDetail() {
  const [product, setProduct] = useState(null);
  const [creator, setCreator] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    if (!id) { navigate(createPageUrl("Shop")); return; }

    async function load() {
      const p = await Product.get ? Product.get(id) : null;
      // fallback: filter by id
      let prod = null;
      try {
        const all = await Product.filter({ id }, "-createdAt", 1);
        prod = all[0];
      } catch {
        const all = await Product.list("-createdAt", 50);
        prod = all.find((x) => x.id === id);
      }
      if (!prod) { navigate(createPageUrl("Shop")); return; }
      setProduct(prod);

      const [creatorData, related] = await Promise.all([
        Creator.filter({ id: prod.creator_id }, "-createdAt", 1).then((r) => r[0]).catch(() => null),
        Product.filter({ creator_id: prod.creator_id, status: "active" }, "-createdAt", 4),
      ]);
      setCreator(creatorData);
      setRelatedProducts(related.filter((r) => r.id !== prod.id).slice(0, 3));
      setLoading(false);
    }
    load();
  }, []);

  const addToCart = () => {
    const cart = JSON.parse(localStorage.getItem("cw_cart") || "[]");
    const existing = cart.find((i) => i.id === product.id);
    if (existing) {
      existing.qty = (existing.qty || 1) + qty;
    } else {
      cart.push({ id: product.id, name: product.name, price: product.price, image: product.images?.[0], creator_name: product.creator_name, qty });
    }
    localStorage.setItem("cw_cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cw_cart_updated"));
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 animate-pulse">
          <div className="aspect-square bg-gray-100 rounded-2xl" />
          <div className="space-y-4">
            <div className="h-4 bg-gray-100 rounded w-1/4" />
            <div className="h-8 bg-gray-100 rounded w-3/4" />
            <div className="h-4 bg-gray-100 rounded w-1/3" />
            <div className="h-20 bg-gray-100 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const discount = product.original_price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-8">
        <Link to={createPageUrl("Home")} className="hover:text-[#c8a96e] transition-colors">Home</Link>
        <span>/</span>
        <Link to={createPageUrl("Shop")} className="hover:text-[#c8a96e] transition-colors">Shop</Link>
        <span>/</span>
        <span className="text-[#1a1a1a] font-medium truncate max-w-[200px]">{product.name}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
        {/* Images */}
        <div>
          <div className="relative aspect-square rounded-2xl overflow-hidden bg-[#f5f3ef] mb-3">
            <img
              src={product.images?.[activeImage]}
              alt={product.name}
              className="w-full h-full object-cover"
            />
            {product.images?.length > 1 && (
              <>
                <button
                  onClick={() => setActiveImage((i) => Math.max(0, i - 1))}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 rounded-full flex items-center justify-center shadow hover:bg-white transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setActiveImage((i) => Math.min(product.images.length - 1, i + 1))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 rounded-full flex items-center justify-center shadow hover:bg-white transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}
            {product.is_new && (
              <span className="absolute top-4 left-4 bg-[#1a1a1a] text-white text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wide">New</span>
            )}
          </div>
          {product.images?.length > 1 && (
            <div className="flex gap-2">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(i)}
                  className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${i === activeImage ? "border-[#c8a96e]" : "border-transparent"}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <p className="text-[#c8a96e] text-xs font-semibold uppercase tracking-widest mb-2">{product.category}</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-3 leading-tight">{product.name}</h1>

          <div className="flex items-center gap-3 mb-4">
            <StarRating rating={product.rating || 0} size="lg" />
            <span className="text-sm text-gray-500">{product.rating} ({product.review_count} reviews)</span>
          </div>

          <div className="flex items-center gap-3 mb-6">
            <span className="font-display text-3xl font-bold text-[#1a1a1a]">${product.price}</span>
            {product.original_price && (
              <>
                <span className="text-lg text-gray-400 line-through">${product.original_price}</span>
                <span className="bg-[#c8a96e] text-white text-xs font-bold px-2 py-0.5 rounded-full">-{discount}%</span>
              </>
            )}
          </div>

          <p className="text-gray-600 leading-relaxed mb-6 text-sm">{product.description}</p>

          {/* Tags */}
          {product.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {product.tags.map((tag) => (
                <span key={tag} className="bg-[#f5f3ef] text-[#6b6b6b] text-xs px-3 py-1 rounded-full">#{tag}</span>
              ))}
            </div>
          )}

          {/* Stock */}
          <div className="flex items-center gap-2 mb-6">
            <Package className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-500">
              {product.stock > 5 ? "In stock" : product.stock > 0 ? `Only ${product.stock} left!` : "Sold out"}
            </span>
          </div>

          {/* Qty + Add to Cart */}
          {product.status !== "sold_out" && product.stock > 0 ? (
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center border border-[#e8e4de] rounded-full overflow-hidden">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-2 text-lg hover:bg-[#f5f3ef] transition-colors">−</button>
                <span className="px-4 py-2 font-medium text-sm min-w-[2rem] text-center">{qty}</span>
                <button onClick={() => setQty((q) => Math.min(product.stock, q + 1))} className="px-4 py-2 text-lg hover:bg-[#f5f3ef] transition-colors">+</button>
              </div>
              <button
                onClick={addToCart}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-full font-semibold text-sm transition-all ${
                  added ? "bg-green-500 text-white" : "bg-[#1a1a1a] hover:bg-[#c8a96e] text-white"
                }`}
              >
                {added ? <><Check className="w-4 h-4" /> Added!</> : <><ShoppingBag className="w-4 h-4" /> Add to Cart</>}
              </button>
            </div>
          ) : (
            <div className="bg-gray-100 text-gray-500 text-center py-3 rounded-full font-medium text-sm mb-6">Sold Out</div>
          )}

          {/* Creator Card */}
          {creator && (
            <Link
              to={createPageUrl(`Creators?id=${creator.id}`)}
              className="flex items-center gap-3 p-4 bg-[#faf9f7] border border-[#e8e4de] rounded-xl hover:border-[#c8a96e] transition-colors"
            >
              <img src={creator.avatar} alt={creator.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400 mb-0.5">Made by</p>
                <p className="font-semibold text-[#1a1a1a] text-sm">{creator.name}</p>
                <p className="text-xs text-[#c8a96e]">{creator.specialty}</p>
              </div>
              {creator.location && (
                <div className="flex items-center gap-1 text-xs text-gray-400 flex-shrink-0">
                  <MapPin className="w-3 h-3" />
                  {creator.location}
                </div>
              )}
            </Link>
          )}
        </div>
      </div>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <div>
          <h2 className="font-display text-2xl font-bold text-[#1a1a1a] mb-6">More from {product.creator_name}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
            {relatedProducts.map((p) => (
              <Link
                key={p.id}
                to={createPageUrl(`ProductDetail?id=${p.id}`)}
                className="group bg-white rounded-xl overflow-hidden border border-[#e8e4de] hover:shadow-md transition-all"
              >
                <div className="aspect-square overflow-hidden bg-[#f5f3ef]">
                  <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-3">
                  <p className="text-xs font-medium text-[#1a1a1a] line-clamp-1 group-hover:text-[#c8a96e] transition-colors">{p.name}</p>
                  <p className="text-sm font-semibold text-[#1a1a1a] mt-1">${p.price}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}